import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import apiService from '../../service/ApiService';
import InitiationShell from './InitiationShell';
import './PocTypePage.scss';

type Justification = {
  title?: string;
  businessDomain?: string;
  abstractText?: string;
  motivation?: string;
  initiationSource?: string;
  expectedValue?: string[];
};

type Scorecard = {
  totalScore?: number;
  band?: string;
  bandTitle?: string;
  bandMessage?: string;
};

type LocationState = {
  pocId?: string;
  pocType?: string;
  businessJustification?: Justification;
  scorecard?: Scorecard;
  scorecardLockMode?: 'SAVE' | 'SAVE_SUBMIT';
  facilitationSupport?: boolean;
  resultAccepted?: boolean;
};

const InitiationReviewPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;
  const justification = state.businessJustification ?? {};
  const scorecard = state.scorecard;

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);


  if (!state.pocType) {
    return (
      <InitiationShell activeStep="review">
        <Section theme="sand" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Review and submit
          </Headline>
          <Lead>Choose a PoC type first (Step 1).</Lead>
          <button
            type="button"
            className="initiation__next"
            onClick={() => navigate('/pocs/new', { replace: true })}
          >
            Go to Step 1
          </button>
        </Section>
      </InitiationShell>
    );
  }

  if (!state.scorecard) {
    return (
      <InitiationShell activeStep="review">
        <Section theme="sand" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Review and submit
          </Headline>
          <Lead>Complete the scorecard first (Steps 3–4).</Lead>
          <button
            type="button"
            className="initiation__next"
            onClick={() => navigate('/pocs/new/scorecard', { state })}
          >
            Go to scorecard
          </button>
        </Section>
      </InitiationShell>
    );
  }

  if (typeof state.facilitationSupport !== 'boolean') {
    return (
      <InitiationShell activeStep="review">
        <Section theme="sand" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Review and submit
          </Headline>
          <Lead>Complete Step 5 first.</Lead>
          <button
            type="button"
            className="initiation__next"
            onClick={() => navigate('/pocs/new/facilitation', { state })}
          >
            Go to facilitation support
          </button>
        </Section>
      </InitiationShell>
    );
  }

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!pocId) {
      setSubmitError(
        'Missing PoC id. Go back to Step 1 and create the draft again.'
      );
      return;
    }
    if (state.resultAccepted !== true) {
      setSubmitError(
        'Score was not accepted. Go back to Step 4 and confirm.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.submitInitiation(pocId, {
        resultAccepted: true,
        facilitationSupportRequested: state.facilitationSupport,
      });
      setSubmitted(true);
      navigate('/pocs/new/preparation/success-criteria', { state });
    } catch (err) {
      console.error(err);
      setSubmitError(
        'Could not submit initiation. Check Network tab (BJ + scorecard must already be saved).'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <InitiationShell activeStep="review">
      <Section theme="light" spacing="tight">
          <Eyebrow>POC Initiation</Eyebrow>
          <Headline as="h1" size="3xl">
            Review and submit
          </Headline>
          <Lead>
            Check your answers, then submit to move this PoC into Preparation.
          </Lead>

          <div className="review-grid">
            <article className="review-card">
              <h3>PoC type</h3>
              <p>
                {state.pocType === 'EXTERNAL_SUPPLIER'
                  ? 'POC with External Supplier'
                  : state.pocType ?? '—'}
              </p>
            </article>

            <article className="review-card">
              <h3>Business justification</h3>
              <p>
                <strong>PoC:</strong> {justification.title || '—'}
              </p>
              <p>
                <strong>Business domain:</strong>{' '}
                {justification.businessDomain || '—'}
              </p>
              <p>
                <strong>Initiation source:</strong>{' '}
                {justification.initiationSource === 'INTERNAL'
                  ? 'Internal'
                  : justification.initiationSource === 'EXTERNAL'
                    ? 'External'
                    : '—'}
              </p>
              <p>
                <strong>Expected value:</strong>{' '}
                {justification.expectedValue?.length
                  ? justification.expectedValue.join(', ')
                  : '—'}
              </p>
            </article>

            <article className="review-card">
              <h3>Scorecard</h3>
              <p>
                Total: {scorecard?.totalScore ?? '—'} ·{' '}
                {scorecard?.bandTitle ?? scorecard?.band ?? '—'}
              </p>
              <p>{scorecard?.bandMessage}</p>
              {state.scorecardLockMode ? (
                <p>
                  Locked via{' '}
                  {state.scorecardLockMode === 'SAVE_SUBMIT'
                    ? 'Save & Submit'
                    : 'Save'}
                  .
                </p>
              ) : null}
            </article>

            <article className="review-card">
              <h3>Facilitation support</h3>
              <p>{state.facilitationSupport ? 'Yes' : 'No'}</p>
            </article>
          </div>

          {submitError ? (
            <p className="bj-field__error">{submitError}</p>
          ) : null}

          {submitted ? (
            <p className="scorecard__notice" role="status">
              Initiation submitted. The PoC is moving to Preparation.
            </p>
          ) : null}

          <div className="initiation__actions initiation__actions--wrap">
            <button
              type="button"
              className="initiation__link-back"
              onClick={() => navigate('/pocs/new/facilitation', { state })}
            >
              ← Back to facilitation
            </button>

            <div className="score-result__buttons">
              <button
                type="button"
                className="initiation__next initiation__next--secondary"
                onClick={() => navigate('/')}
              >
                Back to home
              </button>
              <button
                type="button"
                className="initiation__next"
                disabled={isSubmitting || submitted}
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Submitting…' : 'Submit initiation'}
              </button>
            </div>
          </div>
        </Section>
    </InitiationShell>
  );
};

export default InitiationReviewPage;
